import LeadBlock from "@bmi-digital/components/lead-block";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import Tab from "@mui/material/Tab";
import React from "react";
import RichText from "../../components/RichText";
import { useSiteContext } from "../../components/Site";
import withGTM from "../../utils/google-tag-manager";
import AssetsIframe from "../../components/AssetsIframe";
import { StyledLeadBlockContent } from "../../components/styles/ProductLeadBlock.styles";
import AboutLeadBlock from "./aboutLeadBlock";
import DocumentLeadBlock from "./documentsLeadBlock";
import {
  StyledTabPanel,
  StyledSDPTabLeadBlock,
  StyledSection
} from "./styles/tabLeadBlock.styles";
import TechnicalSpecificationLeadBlock from "./technicalSpecificationLeadBlock";
import { StyledLeadBlock } from "./styles/aboutLeadBlockStyles";
import type { RichTextData } from "../../components/RichText";
import type { TabProps } from "@mui/material/Tab";
import type { System } from "../../types/pim";
import type { TitleWithContentData } from "../../components/TitleWithContent";
import type { SpecificationNotesWithCta } from "./systemDetailsPage";

type Props = {
  system: System;
  aboutLeadBlockGenericContent: TitleWithContentData | null;
  specificationNotes: SpecificationNotesWithCta | null;
  bimDescription: RichTextData | null;
};

const GTMTab = withGTM<TabProps>(Tab, {
  label: "label"
});

const TabLeadBlock = ({
  system,
  aboutLeadBlockGenericContent,
  specificationNotes,
  bimDescription
}: Props) => {
  const { getMicroCopy } = useSiteContext();

  return (
    <StyledSection backgroundColor="white">
      <StyledSDPTabLeadBlock
        initialValue="one"
        tabComponent={(props: TabProps) => (
          <GTMTab
            gtm={{ id: "selector-tabs4</Section>", action: "Selector – Tabs" }}
            {...props}
          />
        )}
      >
        <StyledTabPanel
          heading={getMicroCopy(microCopy.SDP_LEAD_BLOCK_ABOUT)}
          index="one"
          data-testid="tab-panel-lead-block-about"
        >
          <AboutLeadBlock
            system={system}
            sidebarItem={aboutLeadBlockGenericContent}
            data-testid="tab-system-about-lead-block-section"
          />
        </StyledTabPanel>
        {system.classifications?.length && (
          <StyledTabPanel
            heading={getMicroCopy(
              microCopy.SDP_LEAD_BLOCK_TECHNICAL_SPECIFICATION
            )}
            index="two"
            data-testid="tab-panel-lead-block-technical-specification"
          >
            <TechnicalSpecificationLeadBlock
              specificationNotes={specificationNotes}
              technicalSpecClassifications={system.classifications}
            />
          </StyledTabPanel>
        )}
        {system.documents?.length && (
          <StyledTabPanel
            heading={getMicroCopy(
              microCopy.SDP_LEAD_BLOCK_DOCUMENTS_AND_DOWNLOADS
            )}
            index="three"
            data-testid="tab-panel-lead-block-documents-and-downloads"
          >
            <DocumentLeadBlock documents={system.documents} />
          </StyledTabPanel>
        )}
        {system.bim && (
          <StyledTabPanel
            heading={getMicroCopy(microCopy.SDP_TABS_BIM)}
            index="four"
            data-testid="tab-panel-lead-block-bim"
          >
            <StyledLeadBlock data-testid="tab-system-bim-lead-block-section">
              <StyledLeadBlockContent>
                <LeadBlock.Content.Heading hasUnderline={true} variant="h2">
                  {system.bim.name}
                </LeadBlock.Content.Heading>
                <LeadBlock.Content.Section>
                  <RichText document={bimDescription} />
                </LeadBlock.Content.Section>
              </StyledLeadBlockContent>
              <AssetsIframe
                data-testid="bmi-iframe"
                url={system.bim.url}
                title={system.bim.name}
              />
            </StyledLeadBlock>
          </StyledTabPanel>
        )}
      </StyledSDPTabLeadBlock>
    </StyledSection>
  );
};

export default TabLeadBlock;
